import { useState, useEffect } from 'react';
import { Building2 } from 'lucide-react';
import { motion } from 'framer-motion';
import { questionsApi } from '../../../services/api';
import SwotChecklist from './SwotChecklist';

interface SwotSectionProps {
  companyId: string;
  onProgressChange: (progress: number) => void;
}

interface SwotData {
  strengths: string[];
  weaknesses: string[];
  opportunities: string[];
  threats: string[];
}

const swotCategories = [
  {
    key: 'strengths' as keyof SwotData,
    title: 'Strengths & Competitive Assets',
    description: 'Internal factors that give your company advantages',
    color: 'text-green-600',
    bgColor: 'bg-green-50',
    borderColor: 'border-green-200',
    icon: '💪',
    entityType: 'swot.strengths_and_competitive_assets',
  },
  {
    key: 'weaknesses' as keyof SwotData,
    title: 'Weaknesses & Competitive Deficiencies',
    description: 'Internal factors that put your company at a disadvantage',
    color: 'text-red-600',
    bgColor: 'bg-red-50',
    borderColor: 'border-red-200',
    icon: '⚠️',
    entityType: 'swot.weaknesses_and_competitive_deficiencies',
  },
  {
    key: 'opportunities' as keyof SwotData,
    title: 'Market Opportunities',
    description: 'External factors that could give your company advantages',
    color: 'text-blue-600',
    bgColor: 'bg-blue-50',
    borderColor: 'border-blue-200',
    icon: '🎯',
    entityType: 'swot.market_opportunities',
  },
  {
    key: 'threats' as keyof SwotData,
    title: 'External Threats',
    description: 'External factors that could cause trouble for your company',
    color: 'text-orange-600',
    bgColor: 'bg-orange-50',
    borderColor: 'border-orange-200',
    icon: '⚡',
    entityType: 'swot.external_threats',
  },
];

export default function SwotSection({ companyId, onProgressChange }: SwotSectionProps) {
  const [swotData, setSwotData] = useState<SwotData>({
    strengths: [],
    weaknesses: [],
    opportunities: [],
    threats: [],
  });
  const [activeCategory, setActiveCategory] = useState('strengths');
  const [isLoading, setIsLoading] = useState(true);

  // Calculate progress based on answered questions: 5 answered questions = 100% completion
  const calculateProgress = () => {
    const totalItems = Object.values(swotData).reduce((sum, items) => sum + items.length, 0);
    const targetAnswers = 5; // 5 answered questions = 100% completion
    return totalItems >= targetAnswers ? 100 : Math.round((totalItems / targetAnswers) * 100);
  };

  useEffect(() => {
    const progress = calculateProgress();
    onProgressChange(progress);
  }, [swotData]); // Removed onProgressChange from deps - it's memoized with useCallback

  const handleSwotUpdate = (category: keyof SwotData, items: string[]) => {
    setSwotData((prev) => ({
      ...prev,
      [category]: items,
    }));
  };

  // Load SWOT data for all categories
  useEffect(() => {
    const loadSwotData = async () => {
      try {
        setIsLoading(true);

        // Load data for all SWOT categories
        const categoryData: SwotData = {
          strengths: [],
          weaknesses: [],
          opportunities: [],
          threats: [],
        };

        // Load questions for each SWOT category
        for (const category of swotCategories) {
          try {
            const response = await questionsApi.getEntityQuestions(category.entityType, companyId);
            if (response.success && response.data) {
              // Extract selected items from responses
              const selectedItems = response.data
                .filter(
                  (q: any) =>
                    q.responses.length > 0 && q.responses[0].response.booleanValue === true
                )
                .map((q: any) => q.content);

              categoryData[category.key] = selectedItems;
            }
          } catch (error) {
            console.error(`Failed to load ${category.key} data:`, error);
          }
        }

        setSwotData(categoryData);
      } catch (error) {
        console.error('Failed to load SWOT data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadSwotData();
  }, [companyId]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="h-8 w-8 border-2 border-purple-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="strategy-framework-section">
      {/* Summary Bar */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="strategy-framework-summary"
      >
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <span className="text-sm font-medium text-gray-600">Total SWOT Items:</span>
              <span className="inline-flex px-3 py-1 rounded-full text-sm font-medium bg-purple-100 text-purple-800">
                {Object.values(swotData).reduce((sum, items) => sum + items.length, 0)} identified
              </span>
            </div>
            <div className="flex items-center space-x-4 text-xs text-gray-500">
              {swotCategories.map((category) => (
                <div key={category.key} className="flex items-center space-x-1">
                  <span className="text-2xl">{category.icon}</span>
                  <span>{swotData[category.key].length}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </motion.div>

      {/* Two Column Layout - Categories Left, Wider Checklist Right */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="strategy-framework-grid"
      >
        {/* Left Side - Category Navigation */}
        <div className="strategy-framework-sidebar flex flex-col space-y-4">
          {/* Category Tab Navigation */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
            <div className="flex items-center mb-4">
              <Building2 className="h-5 w-5 text-purple-600 mr-2" />
              <h2 className="text-lg font-semibold text-gray-900">SWOT Categories</h2>
              <div className="ml-auto text-right">
                <div className="text-sm text-gray-500">Select a category</div>
              </div>
            </div>

            <div className="space-y-2">
              {swotCategories.map((category) => {
                const itemCount = swotData[category.key].length;
                const hasItems = itemCount > 0;

                return (
                  <button
                    key={category.key}
                    onClick={() => setActiveCategory(category.key)}
                    className={`w-full p-3 rounded-lg text-left transition-all ${
                      activeCategory === category.key
                        ? 'bg-purple-100 border-2 border-purple-300'
                        : 'bg-gray-50 border-2 border-transparent hover:bg-gray-100'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <span className="text-2xl">{category.icon}</span>
                        <div className="flex-1">
                          <div className="text-sm font-medium text-gray-900">{category.title}</div>
                          <div className="text-xs text-gray-600 mt-1">{category.description}</div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2 ml-2">
                        <div
                          className={`w-2 h-2 rounded-full ${hasItems ? 'bg-purple-500' : 'bg-gray-300'}`}
                        />
                        <span
                          className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${
                            hasItems ? 'bg-purple-100 text-purple-800' : 'bg-gray-100 text-gray-600'
                          }`}
                        >
                          {itemCount} items
                        </span>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Category Info Card */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
            <div className="flex items-center space-x-2 mb-3">
              <div className="w-3 h-3 rounded-full bg-purple-500" />
              <h3 className="text-lg font-semibold text-gray-900">
                {swotCategories.find((cat) => cat.key === activeCategory)?.title}
              </h3>
            </div>
            <p className="text-sm text-gray-600 mb-3">
              {swotCategories.find((cat) => cat.key === activeCategory)?.description}
            </p>
            <div className="bg-purple-50 rounded-lg p-3">
              <p className="text-sm text-purple-800">
                Check the boxes that apply to your company in this category. Focus on the most
                significant factors.
              </p>
            </div>
          </div>
        </div>

        {/* Right Side - Wider Checklist */}
        <div className="xl:col-span-3">
          {swotCategories.map((category) => (
            <div
              key={category.key}
              className={activeCategory === category.key ? 'block h-full' : 'hidden'}
            >
              <SwotChecklist
                entityType={category.entityType}
                companyId={companyId}
                selectedItems={swotData[category.key]}
                onSelectionChange={(items: string[]) => handleSwotUpdate(category.key, items)}
                color={category.color}
              />
            </div>
          ))}
        </div>
      </motion.div>
    </div>
  );
}
